/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { FC } from 'react';
import { FormControlLabel } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { AppVariant, Contract, ListType, Tenant, TreeNodeItem } from '../../types';
import useTreeList from '../hooks/useTreeList';
import TreeListNodeCheckbox from './TreeListNodeCheckbox';

const useStyles = makeStyles(theme => ({
	root: {
		marginLeft: 10,
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		textAlign: 'left',
		color: theme.palette.primary.dark,
		'& > .MuiFormControlLabel-root': {
			width: '100%',
			justifyContent: (props: TreeListNodeLabelProps): string =>
				props.type === ListType.CONTRACT ? 'space-between' : 'flex-start',
			color: theme.palette.primary.dark
		},
		'& > .MuiFormControlLabel-labelPlacementStart': {
			marginLeft: -10
		}
	},
	checkbox: {
		padding: 0,
		marginRight: 6
	}
}));

type TreeListNodeLabelProps = TreeNodeItem<AppVariant | Tenant | Contract> & {
	showCheckbox?: boolean;
};

const TreeListNodeLabel: FC<TreeListNodeLabelProps> = ({ type, id, name }: TreeListNodeLabelProps) => {
	const classes = useStyles({ type, id, name });
	const { onLabelClick, onCheckboxClick, isFilterSet, isContractSelected } = useTreeList();

	const handleLabelClick = (event: React.MouseEvent<HTMLLabelElement, MouseEvent>): void => {
		event.preventDefault();
		onLabelClick({ id, name, type });
	};

	const isChecked = ListType.CONTRACT === type ? isContractSelected(id) : isFilterSet(type, id);

	return (
		<div id={id} className={classes.root}>
			<FormControlLabel
				control={
					<TreeListNodeCheckbox
						isChecked={isChecked}
						className={classes.checkbox}
						id={id}
						name={name}
						type={type}
						onClick={onCheckboxClick}
					/>
				}
				labelPlacement={type === ListType.CONTRACT ? 'start' : 'end'}
				onClick={handleLabelClick}
				label={name}
			/>
		</div>
	);
};

export default TreeListNodeLabel;
